'use client';

import Image from 'next/image';
import { CallToActionButton } from '@/components/shared/CallToActionButton';

import { cn } from '@/app/utils/cn';
import Badge from '../shared/badge';
import { DotBlockEditor } from '../shared/decodeTextBlock';
import { renderersHeading, renderersSubheading } from '../shared/animations';
import checkTruthyValue from '@/app/utils/checkTruthyValue';
import safeJsonParse from '@/app/utils/safeJSONParse';
import { IconCheck } from '@tabler/icons-react';
import { HubspotFormEmbed } from './HubspotForm';
import { decode } from 'html-entities';

function renderBackgroundImage(backgroundImage) {
    if (!backgroundImage) return null;
    return (
        <div className="absolute inset-0 -z-10 hidden sm:block">
            <div
                className="absolute right-0 top-0 h-full w-1/2 bg-contain bg-center bg-no-repeat"
                style={{
                    backgroundImage: `url('/da/${backgroundImage?.idPath ?? backgroundImage}')`
                }}
            />
        </div>
    );
}

function gridBgImages(imagePrimaryLoad) {
    const positions = ['right-0 top-[-200px]', 'bottom-0 left-[20%]'];

    return (
        <>
            {positions.map((pos, idx) => (
                <div key={idx} className={`pointer-events-none absolute -z-50 ${pos}`}>
                    <Image
                        src="/assets/grid-dots.svg"
                        alt={`grid dots ${idx}`}
                        width={670}
                        height={430}
                        priority={!!imagePrimaryLoad}
                        unoptimized
                    />
                </div>
            ))}
        </>
    );
}

function GlobalGridWrapper({ children, addGridBgImages, halfWidth }) {
    const hasGridBgImages = checkTruthyValue(addGridBgImages);
    return (
        <div
            className={cn(
                'grid container grid-cols-[repeat(auto-fit,minmax(16rem,1fr))] sm:grid-cols-[repeat(auto-fit,minmax(20rem,1fr))] justify-start gap-6 md:gap-12 lg:gap-20 xl:gap-24 row-default-p',
                hasGridBgImages && 'h-screen max-h-[800px]',
                halfWidth && 'md:w-[50%]'
            )}>
            {children}
        </div>
    );
}

function Layout01(props) {
    const {
        image,
        preheading,
        heading,
        heading_raw,
        subheading,
        subheading_raw,
        callToAction = [],
        imagePrimaryLoad,
        backgroundImage,
        addGridBgImages,
        halfWidth,
        headingComponent
    } = props;
    //TODO: FIX IMAGE ISSUE HERE IDENTIFIER IS NOT AVAILABLE
    const hasGridBgImages = checkTruthyValue(addGridBgImages);
    // Use _raw values as fallback when heading/subheading are null
    const parsedHeading = heading ?? safeJsonParse(heading_raw);
    const parsedSubheading = subheading ?? safeJsonParse(subheading_raw);

    return (
        <>
            <GlobalGridWrapper halfWidth={halfWidth} addGridBgImages={addGridBgImages}>
                <div className="flex flex-col items-start gap-4">
                    {preheading && (
                        <h1 className="flex items-center gap-2">
                            <Badge>{preheading}</Badge>
                        </h1>
                    )}
                    {parsedHeading && (
                        <DotBlockEditor
                            customRenderers={renderersHeading(headingComponent)}
                            blocks={parsedHeading}
                        />
                    )}
                    {parsedSubheading && (
                        <DotBlockEditor
                            customRenderers={renderersSubheading(headingComponent)}
                            blocks={parsedSubheading}
                        />
                    )}
                    {callToAction.length > 0 && (
                        <div className="flex flex-wrap gap-4 [&>*]:!mx-0 [&>*]:!w-full sm:[&>*]:!w-fit">
                            {callToAction.map(
                                (cta, index) =>
                                    cta.urlName &&
                                    cta.url && (
                                        <CallToActionButton
                                            key={`cta-${index}-${cta.url}`}
                                            {...cta}>
                                            {cta.urlName}
                                        </CallToActionButton>
                                    )
                            )}
                        </div>
                    )}
                </div>
                {image && (
                    <Image
                        src={image?.fileAsset?.idPath ?? image}
                        alt="image"
                        width={0}
                        height={0}
                        sizes="(max-width: 1200px) calc(100vw - 32px), 1200px"
                        quality={50}
                        priority={!!imagePrimaryLoad}
                        className="h-auto w-full"
                    />
                )}
            </GlobalGridWrapper>
            {renderBackgroundImage(backgroundImage)}
            {hasGridBgImages && gridBgImages(imagePrimaryLoad)}
        </>
    );
}

function Layout02({
    preheading,
    heading,
    heading_raw,
    subheading,
    subheading_raw,
    callToAction = [],
    backgroundImage,
    imagePrimaryLoad,
    addGridBgImages,
    halfWidth,
    headingComponent
}) {
    // Use _raw values as fallback when heading/subheading are null
    const parsedHeading = heading ?? safeJsonParse(heading_raw);
    const parsedSubheading = subheading ?? safeJsonParse(subheading_raw);

    return (
        <>
            <div
                className={cn(
                    `flex flex-col items-start gap-6`,
                    addGridBgImages && 'h-screen max-h-[800px]',
                    halfWidth && 'md:w-[50%]'
                )}>
                {preheading && (
                    <h1>
                        <Badge>{preheading}</Badge>
                    </h1>
                )}
                {parsedHeading && (
                    <DotBlockEditor
                        customRenderers={renderersHeading(headingComponent)}
                        blocks={parsedHeading}
                    />
                )}
                {parsedSubheading && (
                    <DotBlockEditor
                        customRenderers={renderersSubheading(headingComponent)}
                        blocks={parsedSubheading}
                    />
                )}
                {callToAction.length > 0 && (
                    <div className="flex flex-wrap gap-4 [&>*]:!mx-0 [&>*]:!w-full sm:[&>*]:!w-fit">
                        {callToAction.map(
                            (cta, index) =>
                                cta.urlName &&
                                cta.url && (
                                    <CallToActionButton key={`cta-${index}-${cta.url}`} {...cta}>
                                        {cta.urlName}
                                    </CallToActionButton>
                                )
                        )}
                    </div>
                )}
            </div>
            {renderBackgroundImage(backgroundImage)}
            {addGridBgImages && gridBgImages(imagePrimaryLoad)}
        </>
    );
}

function Layout03({
    heading,
    heading_raw,
    subheading,
    subheading_raw,
    callToAction = [],
    preheading,
    backgroundImage,
    imagePrimaryLoad,
    addGridBgImages,
    halfWidth,
    headingComponent,
    _map
}) {
    const hasGridBgImages = checkTruthyValue(addGridBgImages);

    // Use _raw values as fallback when heading/subheading are null
    const parsedHeading = heading ?? safeJsonParse(heading_raw);
    const parsedSubheading = subheading ?? safeJsonParse(subheading_raw);

    return (
        <>
            <div
                className={cn(
                    'm-auto flex w-full flex-col items-center justify-center gap-6 text-center row-default-p',
                    hasGridBgImages && 'h-screen max-h-[800px]',
                    halfWidth && 'md:w-[50%]'
                )}>
                {preheading && (
                    <h1>
                        <Badge>{preheading}</Badge>
                    </h1>
                )}
                {parsedHeading && (
                    <DotBlockEditor
                        customRenderers={renderersHeading(headingComponent)}
                        blocks={parsedHeading}
                    />
                )}
                {parsedSubheading && (
                    <DotBlockEditor
                        customRenderers={renderersSubheading(headingComponent)}
                        blocks={parsedSubheading}
                    />
                )}
                {callToAction.length > 0 && (
                    <div className="flex flex-wrap justify-center gap-4 [&>*]:mx-auto sm:[&>*]:mx-0">
                        {callToAction.map(
                            (cta, index) =>
                                cta.urlName &&
                                cta.url && (
                                    <CallToActionButton key={`cta-${index}-${cta.url}`} {...cta}>
                                        {cta.urlName}
                                    </CallToActionButton>
                                )
                        )}
                    </div>
                )}
            </div>
            {renderBackgroundImage(backgroundImage)}
            {hasGridBgImages && gridBgImages(imagePrimaryLoad)}
        </>
    );
}

function Layout04({
    image,
    preheading,
    heading,
    heading_raw,
    subheading,
    subheading_raw,
    callToAction = [],
    imagePrimaryLoad,
    backgroundImage,
    addGridBgImages,
    halfWidth,
    headingComponent
}) {
    // Use _raw values as fallback when heading/subheading are null
    const parsedHeading = heading ?? safeJsonParse(heading_raw);
    const parsedSubheading = subheading ?? safeJsonParse(subheading_raw);

    return (
        <>
            <div
                className={cn(
                    'grid grid-cols-[7fr_5fr] items-center gap-8',
                    addGridBgImages && 'h-screen max-h-[800px]',
                    halfWidth && 'md:w-[50%]'
                )}>
                <div className="flex flex-col items-start gap-6">
                    {preheading && (
                        <h1 className="inline-block text-sm font-semibold uppercase tracking-widest text-gray-500">
                            {preheading}
                        </h1>
                    )}
                    {parsedHeading && (
                        <DotBlockEditor
                            customRenderers={renderersHeading(headingComponent)}
                            blocks={parsedHeading}
                        />
                    )}
                    {parsedSubheading && (
                        <DotBlockEditor
                            customRenderers={renderersSubheading(headingComponent)}
                            blocks={parsedSubheading}
                        />
                    )}
                    {callToAction.length > 0 && (
                        <div className="flex flex-wrap gap-4 [&>*]:!mx-0 [&>*]:!w-full sm:[&>*]:!w-fit">
                            {callToAction.map(
                                (cta, index) =>
                                    cta.urlName &&
                                    cta.url && (
                                        <CallToActionButton
                                            key={`cta-${index}-${cta.url}`}
                                            {...cta}>
                                            {cta.urlName}
                                        </CallToActionButton>
                                    )
                            )}
                        </div>
                    )}
                </div>
                {image && (
                    <Image
                        src={image?.idPath ?? image}
                        alt="image"
                        width={0}
                        height={0}
                        sizes="(max-width: 600px) calc(100vw - 32px), 600px"
                        quality={50}
                        className="h-auto w-full"
                        priority={!!imagePrimaryLoad}
                    />
                )}
            </div>
            {renderBackgroundImage(backgroundImage)}
            {addGridBgImages && gridBgImages(imagePrimaryLoad)}
        </>
    );
}

// Custom renderers for Layout05's subheading — the block editor field carries
// both the intro paragraph and a bulletList that renders as a 2-col checklist.
function renderInline(content) {
    if (!Array.isArray(content)) return null;
    return content.map((item, i) => {
        if (item?.type !== 'text') return null;
        const text = decode(item.text ?? '');
        if (item.marks?.some((m) => m.type === 'bold')) {
            return <strong key={i}>{text}</strong>;
        }
        return <span key={i}>{text}</span>;
    });
}

const layout05Renderers = {
    paragraph: ({ node }) => {
        if (!node?.content?.length) return null;
        return (
            <p className="text-base leading-7 text-slate-500 lg:text-lg lg:leading-8">
                {renderInline(node.content)}
            </p>
        );
    },
    bulletList: ({ node }) => {
        if (!node?.content?.length) return null;
        return (
            <ul className="mt-8 grid list-none gap-3 p-0 text-sm font-medium sm:grid-cols-2">
                {node.content.map((li, i) => {
                    const paragraph = li?.content?.[0];
                    const inline = paragraph?.content;
                    return (
                        <li key={i} className="flex items-start gap-3">
                            <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-fuschia-100 text-fuschia-700">
                                <IconCheck size={16} />
                            </span>
                            <span className="text-slate-900">{renderInline(inline)}</span>
                        </li>
                    );
                })}
            </ul>
        );
    }
};

function Layout05({
    preheading,
    heading,
    heading_raw,
    subheading,
    subheading_raw,
    hubspotFormCode,
    identifier
}) {
    const parsedHeading = heading ?? safeJsonParse(heading_raw);
    const parsedSubheading = subheading ?? safeJsonParse(subheading_raw);

    // Unique target id so multiple HubSpot forms on the same page don't collide.
    const formTargetId = `hubspot-form-${identifier ?? 'banner'}`;

    return (
        <div className="container mx-auto grid gap-12 px-4 pb-14 pt-6 lg:grid-cols-[minmax(0,1fr)_500px] lg:pb-20 lg:pt-8">
            <div className="max-w-3xl pt-2">
                {preheading && (
                    <p className="mb-5 text-sm font-semibold uppercase tracking-wide text-fuschia-700">
                        {preheading}
                    </p>
                )}
                {parsedHeading && (
                    <div className="prose max-w-none prose-headings:m-0 prose-h1:text-5xl prose-h1:font-semibold prose-h1:leading-tight md:prose-h1:text-6xl prose-h2:text-5xl prose-h2:font-semibold prose-h2:leading-tight md:prose-h2:text-6xl">
                        <DotBlockEditor blocks={parsedHeading} />
                    </div>
                )}
                {parsedSubheading && (
                    <div className="mt-6 flex max-w-2xl flex-col gap-8">
                        <DotBlockEditor
                            blocks={parsedSubheading}
                            customRenderers={layout05Renderers}
                        />
                    </div>
                )}
            </div>

            <aside className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-[0_20px_50px_-24px_rgba(12,18,49,0.18)]">
                <HubspotFormEmbed
                    hubspotFormCode={hubspotFormCode}
                    targetId={formTargetId}
                    className="min-h-[420px]"
                />
            </aside>
        </div>
    );
}

const layoutComponents = {
    1: Layout01,
    2: Layout02,
    3: Layout03,
    4: Layout04,
    5: Layout05
};

function Banner(props) {
    const LayoutComponent = layoutComponents[props.layout] || Layout01;
    return <LayoutComponent data-layout={props.layout} data-component="banner" {...props} />;
}

export default Banner;
